import type { LucideIcon } from 'lucide-react'
import {
	Building2,
	LayoutDashboard,
	CreditCard,
	LayoutTemplate,
	ListChecks,
	PenLine,
	Plus,
	Plug,
	ScrollText,
	ShieldCheck,
	UserRound,
	Users,
	UsersRound,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { type ComponentProps, Suspense, useState } from 'react'
import { toast } from 'sonner'
import { type NavigateFunction, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useRouteSession } from '@web/app'
import { BrandMark } from '@layerit/ui/components/brand-mark'
import { PageContent } from '@layerit/ui/components/page-content'
import { GlobalSearch, type SearchDestination } from '@layerit/ui/components/global-search'
import { OrganisationMark, OrganisationSwitcher, useSwitchOrganisation } from '@web/components/organisation-switcher'
import { ProfileMenu } from '@web/components/profile-menu'
import { ThemeMenu } from '@layerit/ui/components/theme-menu'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@layerit/ui/components/ui/collapsible'
import { Skeleton } from '@layerit/ui/components/ui/skeleton'
import {
	Sidebar,
	SidebarContent,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarInset,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarProvider,
	SidebarRail,
	SidebarTrigger,
	useSidebar,
} from '@layerit/ui/components/ui/sidebar'
import { useRouteName } from '@web/hooks'
import { organizationsQuery } from '@web/hooks/organizations'
import { followLink } from '@web/lib/navigation'
import type { RouteName } from '@web/router'
import { ROUTES, isConsolePath, isSafeRedirectPath } from '@web/router'
import { APP_NAME } from '@layerit/brand'

interface NavItem {
	to: string
	route: RouteName
	label: string
	icon: LucideIcon
}

/** The words people type for a page that its label does not contain. */
const SEARCH_KEYWORDS: Partial<Record<RouteName, readonly string[]>> = {
	getStarted: ['setup', 'onboarding', 'home', 'get started', 'overview'],
	signatures: ['designer', 'editor', 'signature', 'design', 'draft', 'publish'],
	rules: ['routing', 'conditions'],
	identities: ['people', 'users', 'directory', 'mailboxes'],
	connections: ['providers', 'google', 'microsoft', 'zoho', 'integrations'],
	settingsAccount: ['profile', 'account', 'passkeys', 'sessions', 'sign out'],
	settingsOrganisation: ['company', 'domains'],
	settingsBilling: ['plan', 'invoices', 'payment', 'subscription'],
	settingsUsers: ['team', 'members', 'invite'],
	settingsRoles: ['permissions', 'access'],
	settingsAudit: ['log', 'history', 'events'],
}

function Wordmark() {
	return (
		<>
			<BrandMark size={28} />
			<span className="text-sm font-semibold tracking-tight group-data-[collapsible=icon]:hidden">{APP_NAME}</span>
		</>
	)
}

function SidebarLink({ to, navigate, ...props }: ComponentProps<'a'> & { to: string; navigate: NavigateFunction }) {
	const { setOpenMobile } = useSidebar()

	return (
		<a
			{...props}
			href={to}
			onClick={(event) => {
				followLink(event, to, navigate)
				if (event.defaultPrevented) setOpenMobile(false)
			}}
		/>
	)
}

export function ConsoleLayout() {
	const { state, signOut, reload, can } = useRouteSession()
	const routeName = useRouteName()
	const { pathname } = useLocation()
	const navigate = useNavigate()

	/**
	 * Whether the Settings group has been opened by hand.
	 *
	 * Declared here, above the early returns below, because a hook after a conditional return
	 * changes the number of hooks between renders — React's own rules, and `useHookAtTopLevel`
	 * fails the build for it.
	 */
	const [settingsOpened, setSettingsOpened] = useState(false)

	/* The designer needs the width, so the sidebar folds to icons there and opens again on leaving. */
	const designing = pathname.startsWith(`${ROUTES.signatures}/`)
	const [sidebarOpen, setSidebarOpen] = useState(true)
	const [designerSidebarOpen, setDesignerSidebarOpen] = useState(false)

	/* The organisations the search can switch into. Same reason for living up here as the
	 * state above, and only fetched for a member: the invitation screen shares this layout. */
	const organizations = useQuery({ ...organizationsQuery(), enabled: state.status === 'ready' })
	const switchOrganisation = useSwitchOrganisation(reload, navigate)

	if (state.status === 'organisation' && isConsolePath(pathname) && pathname !== ROUTES.join && routeName !== 'invitation') {
		const target = isSafeRedirectPath(pathname) ? pathname : ROUTES.getStarted

		const to =
			state.displayName === null
				? `${ROUTES.onboardingName}?redirect=${encodeURIComponent(target)}`
				: `${ROUTES.chooseOrganisation}?redirect=${encodeURIComponent(target)}`
		return <Navigate to={to} replace />
	}

	if (state.status !== 'ready' && state.status !== 'organisation') return null

	const ready = state.status === 'ready'

	const navItems: NavItem[] = [
		{ to: ROUTES.getStarted, route: 'getStarted', label: 'Dashboard', icon: LayoutDashboard },
		{ to: ROUTES.connections, route: 'connections', label: 'Connections', icon: Plug },
		{ to: ROUTES.signatures, route: 'signatures', label: 'Signatures', icon: PenLine },
		{ to: ROUTES.identities, route: 'identities', label: 'Coverage', icon: Users },
		{ to: ROUTES.templates, route: 'templates', label: 'Templates', icon: LayoutTemplate },
		{ to: ROUTES.rules, route: 'rules', label: 'Rules', icon: ListChecks },
	]

	/**
	 * Settings — the highest-privilege area, and one page per item.
	 *
	 * A group that opens **in place** rather than a second rail: the console already has one
	 * sidebar, and a nested column beside it would put two navigations in competition for the
	 * same glance. Each item is still its own route, so the sidebar, the page's own heading and
	 * the browser's back button all agree about where the person is.
	 *
	 * The order is what a person reaches for, not the order of privilege: their own account
	 * first, then the organisation's identity, the people and the money that follows from
	 * them, the roles that govern them, and the audit trail last — the screen you read
	 * *after* something has gone wrong.
	 */
	const settingsItems: NavItem[] = [
		{ to: ROUTES.settingsAccount, route: 'settingsAccount', label: 'My Account', icon: UserRound },
		{ to: ROUTES.settingsOrganisation, route: 'settingsOrganisation', label: 'Organisation', icon: Building2 },
		{ to: ROUTES.settingsBilling, route: 'settingsBilling', label: 'Plans and Billing', icon: CreditCard },
	]

	/* Each item is offered only to somebody the page behind it can actually serve — the
	 * permission is the one the route's own API enforces, so a visible item is never a link
	 * that answers 403. An Auditor keeps the audit trail, which is the whole point of the
	 * role; the write controls inside the pages are what they do not get. */
	if (can('members:read')) {
		settingsItems.push(
			{ to: ROUTES.settingsUsers, route: 'settingsUsers', label: 'Users', icon: UsersRound },
			{ to: ROUTES.settingsRoles, route: 'settingsRoles', label: 'Roles and Permissions', icon: ShieldCheck },
		)
	}

	if (can('audit:read')) {
		settingsItems.push({ to: ROUTES.settingsAudit, route: 'settingsAudit', label: 'Audit Log', icon: ScrollText })
	}

	/**
	 * The group is open whenever the person is inside it, and remembers being opened when they
	 * are not.
	 *
	 * `defaultOpen` alone would be wrong: the layout mounts once, so somebody who arrives at
	 * `/settings/billing` by link and later navigates within the console would find the group
	 * shut with the current page hidden inside it.
	 */
	const insideSettings = routeName === 'settings' || settingsItems.some((item) => item.route === routeName)
	const settingsOpen = settingsOpened || insideSettings

	/* Everywhere the sidebar can take this person, for the search in the bar. Built from the
	 * same two lists, so the search never offers a page the sidebar withholds. */
	const searchDestinations: SearchDestination[] = [
		...navItems.map((item) => ({ ...item, group: 'Console', keywords: SEARCH_KEYWORDS[item.route] })),
		...settingsItems.map((item) => ({ ...item, group: 'Settings', keywords: SEARCH_KEYWORDS[item.route] })),
	]

	/* Every other organisation this account belongs to, switched into through the same server
	 * handshake as the sidebar switcher. A failure has no menu to show it in here, so it toasts. */
	if (ready) {
		for (const organization of organizations.data ?? []) {
			if (organization.id === state.user.tenantId) continue
			searchDestinations.push({
				to: `organisation:${organization.id}`,
				label: organization.name,
				group: 'Switch organisation',
				icon: Building2,
				mark: <OrganisationMark name={organization.name} current={false} className="size-5 rounded-sm text-[9px]" />,
				keywords: ['organisation', 'organization', 'switch', organization.slug],
				onSelect: () =>
					switchOrganisation.mutate(organization.id, {
						onError: (error) => toast.error(error.message || 'That organisation could not be opened.'),
					}),
			})
		}
		searchDestinations.push({
			to: ROUTES.newOrganisation,
			label: 'Create organisation',
			group: 'Switch organisation',
			icon: Plus,
			keywords: ['new', 'add', 'organisation', 'organization'],
		})
	}

	return (
		<SidebarProvider
			open={designing ? designerSidebarOpen : sidebarOpen}
			onOpenChange={designing ? setDesignerSidebarOpen : setSidebarOpen}
		>
			<Sidebar collapsible="icon">
				<SidebarHeader>
					<SidebarMenu>
						<SidebarMenuItem>
							<SidebarMenuButton
								size="lg"
								tooltip={APP_NAME}
								render={<SidebarLink to={ROUTES.getStarted} navigate={navigate} />}
							>
								<Wordmark />
							</SidebarMenuButton>
						</SidebarMenuItem>
					</SidebarMenu>
					{/* Only a member has an organisation to show: the invitation screen also renders
					 * in this layout, for somebody who has not joined one yet. */}
					{ready && <OrganisationSwitcher activeId={state.user.tenantId} reload={reload} navigate={navigate} />}
				</SidebarHeader>

				<SidebarContent>
					<SidebarGroup>
						<SidebarGroupLabel>Console</SidebarGroupLabel>
						<SidebarGroupContent>
							<SidebarMenu>
								{navItems.map((item) => {
									const active = routeName === item.route
									return (
										<SidebarMenuItem key={item.to}>
											<SidebarMenuButton
												isActive={active}
												aria-current={active ? 'page' : undefined}
												tooltip={item.label}
												render={<SidebarLink to={item.to} navigate={navigate} />}
											>
												<item.icon />
												<span>{item.label}</span>
											</SidebarMenuButton>
										</SidebarMenuItem>
									)
								})}
							</SidebarMenu>
						</SidebarGroupContent>
					</SidebarGroup>

					{settingsItems.length > 0 && (
						<SidebarGroup>
							<SidebarGroupLabel>Settings</SidebarGroupLabel>
							<SidebarGroupContent>
								<Collapsible open={settingsOpen} onOpenChange={setSettingsOpened}>
									<SidebarMenu>
										<SidebarMenuItem>
											{/* The trigger is the group's own heading, so it names the
											 * area rather than the verb: "Settings", not "Toggle
											 * settings". It carries the current child's label as its
											 * tooltip when the rail is collapsed to icons, which is
											 * otherwise the one state where a parent item says
											 * nothing about where you are. */}
											<CollapsibleTrigger
												render={
													<SidebarMenuButton
														isActive={insideSettings}
														tooltip={
															settingsItems.find((item) => item.route === routeName)?.label ?? 'Settings'
														}
													/>
												}
											>
												<ShieldCheck />
												<span>Settings</span>
											</CollapsibleTrigger>

											<CollapsibleContent>
												<SidebarMenuSub>
													{settingsItems.map((item) => {
														const active = routeName === item.route
														return (
															<SidebarMenuSubItem key={item.to}>
																<SidebarMenuSubButton
																	isActive={active}
																	aria-current={active ? 'page' : undefined}
																	render={<SidebarLink to={item.to} navigate={navigate} />}
																>
																	<span>{item.label}</span>
																</SidebarMenuSubButton>
															</SidebarMenuSubItem>
														)
													})}
												</SidebarMenuSub>
											</CollapsibleContent>
										</SidebarMenuItem>
									</SidebarMenu>
								</Collapsible>
							</SidebarGroupContent>
						</SidebarGroup>
					)}
				</SidebarContent>

				<SidebarRail />
			</Sidebar>

			{/* `min-w-0` all the way down: a flex child defaults to the width of its widest content,
			 * so one long audit line or a wide comparison would otherwise push the whole page past
			 * the viewport and scroll the body sideways under the sidebar. Bounded here, the page
			 * keeps to the space beside the sidebar, and anything wider scrolls inside its own
			 * container (tables) or truncates (rows). `overflow-x-clip` on both, rather than
			 * `hidden`, so neither becomes a scroll container and sticky headers and footers still
			 * stick; the inset needs it as well because sticky table cells report their overflow past
			 * the inner one. */}
			<SidebarInset className="min-w-0 overflow-x-clip bg-background">
				<header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-background px-6">
					<SidebarTrigger className="-ml-1" />
					<GlobalSearch destinations={searchDestinations} navigate={navigate} className="ml-1" />
					<div className="ml-auto flex items-center gap-1">
						<ThemeMenu />
						{ready && <ProfileMenu user={state.user} signOut={signOut} navigate={navigate} />}
					</div>
				</header>

				<main className="min-w-0 flex-1 overflow-x-clip">
					{/* Each screen is its own chunk (see `routes.tsx`), so the first visit to one
					 * waits on a fetch; the shell stays put and the page area shows its outline. */}
					<Suspense fallback={<ScreenFallback />}>
						<Outlet />
					</Suspense>
				</main>
			</SidebarInset>
		</SidebarProvider>
	)
}

export function ScreenFallback() {
	return (
		<PageContent>
			<div role="status" aria-busy="true" aria-label="Loading" className="flex flex-col gap-6">
				<Skeleton className="h-7 w-48" />
				<Skeleton className="h-64 w-full" />
			</div>
		</PageContent>
	)
}
